/* A module-level store rather than a context: toasts get raised from event
   handlers, socket frames and catch blocks all over the app, and threading a
   hook through every one of them buys nothing. <Toasts/> subscribes. */

export type ToastKind = "" | "ok" | "error";

/* One button on a toast, for the actions that are better undone than
   confirmed. A dialog in front of `skip` would cost a click on every press to
   save the rare mis-click; this costs nothing until something goes wrong. */
export interface ToastAction {
  label: string;
  run: () => void;
}

export interface Toast {
  id: number;
  text: string;
  kind: ToastKind;
  action?: ToastAction;
}

const LIFETIME_MS = 6000;
/* An undo nobody has time to press is not an undo. */
const ACTION_LIFETIME_MS = 12000;

let toasts: Toast[] = [];
let seq = 0;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

export function toast(
  text: string,
  kind: ToastKind = "",
  action?: ToastAction,
): void {
  const id = ++seq;
  toasts = [...toasts, { id, text, kind, action }];
  emit();
  setTimeout(() => dismissToast(id), action ? ACTION_LIFETIME_MS : LIFETIME_MS);
}

export function dismissToast(id: number): void {
  const before = toasts.length;
  toasts = toasts.filter((item) => item.id !== id);
  if (toasts.length !== before) emit();
}

const toastedOnce = new Set<string>();

/** For conditions that stay true - "you are muted" would otherwise fire on
    every bootstrap and every quota refresh. */
export function toastOnce(key: string, text: string, kind: ToastKind = ""): void {
  if (toastedOnce.has(key)) return;
  toastedOnce.add(key);
  toast(text, kind);
}

export function subscribeToasts(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function toastSnapshot(): Toast[] {
  return toasts;
}
